import type { AppStore } from "@/store";
import { trackEvent, TrackedEvent, trackProactiveAccessEnabled } from "@/domains/metrics";
import { buildProactivePaywallEventPayload } from "@/domains/metrics/monetization";
import { logger } from "@/modules/logger";
import { prepareFloatingMemForOnboarding } from "@/modules/mem-agent/prepare-floating-mem-for-onboarding";
import { objectModule } from "@/modules/object";
import {
  clearPendingReminderShillUpsellAttribution,
  dismissReminderShillUpsellAttributionContext,
  readPendingReminderShillUpsellAttribution,
  recordPendingReminderShillUpsellAttribution,
} from "@/modules/reminder-shill-upsell-attribution";
import { toastModule } from "@/modules/toast";
import {
  StripeCheckoutOnboardingStep,
  type StripeCheckoutOnboardingStep as StripeCheckoutOnboardingStepType,
} from "@/modules/subscriptions/query-params";
import { PROACTIVE_PRICING_CLIENT_FALLBACK_SOURCE } from "@/store/subscriptions";
import {
  SubscriptionPlanKind,
  type SubscriptionPlanKind as SubscriptionPlanKindType,
} from "@/store/subscriptions/types";

import { confirmStripeCheckoutReturn } from "./stripe-checkout-return-confirmation";

export const PROACTIVE_SUBSCRIPTION_RETURN_NOT_CONFIRMED_MESSAGE =
  "Finish upgrading to Proactive before setting up Mem Agent.";
export const PROACTIVE_SUBSCRIPTION_RETURN_CANCELED_MESSAGE = "Proactive upgrade canceled.";

/**
 * Decides whether a Proactive checkout return belongs to the in-app onboarding flow.
 *
 * `checkoutStartedFromOnboarding` comes from the Stripe return URL so the
 * decision survives subscription reconciliation and onboarding initialization.
 */
const checkoutReturnBelongsToOnboarding = ({
  checkoutOnboardingStep,
  checkoutStartedFromOnboarding = false,
  store,
}: {
  checkoutOnboardingStep?: StripeCheckoutOnboardingStepType;
  checkoutStartedFromOnboarding?: boolean;
  store: AppStore;
}): boolean => {
  if (checkoutStartedFromOnboarding || checkoutOnboardingStep !== undefined) {
    return true;
  }

  if (
    store.onboarding.currentStepId === "proactive-trial" ||
    store.onboarding.currentStepId === "existing-user-proactive-trial"
  ) {
    return true;
  }

  const context = store.onboarding.context;
  if (
    !context.isEligibleForProactiveTrialOnboarding ||
    context.hasCompletedOrSkippedProactiveTrialOnboarding
  ) {
    return false;
  }

  return (
    context.proactiveTrialOnboardingDebugOverride || context.proactiveTrialOnboardingIsRelevant
  );
};

/** Identifies the legacy-user Proactive offer, which resumes onboarding via its own welcome. */
const checkoutReturnBelongsToExistingUserProactiveTrial = ({
  checkoutOnboardingStep,
  store,
}: {
  checkoutOnboardingStep?: StripeCheckoutOnboardingStepType;
  store: AppStore;
}): boolean =>
  checkoutOnboardingStep === StripeCheckoutOnboardingStep.ExistingUserProactiveTrial ||
  store.onboarding.currentStepId === "existing-user-proactive-trial";

const resolveProactiveConversionCheckoutSource = ({
  checkoutBelongsToExistingUserProactiveTrial,
  checkoutBelongsToOnboarding,
  checkoutStartedFromUpgradePlanFlow,
}: {
  checkoutBelongsToExistingUserProactiveTrial: boolean;
  checkoutBelongsToOnboarding: boolean;
  checkoutStartedFromUpgradePlanFlow: boolean;
}): "existing_user_onboarding" | "onboarding" | "upgrade_plan" | "upsell" => {
  if (checkoutBelongsToExistingUserProactiveTrial) {
    return "existing_user_onboarding";
  }

  if (checkoutBelongsToOnboarding) {
    return "onboarding";
  }

  if (checkoutStartedFromUpgradePlanFlow) {
    return "upgrade_plan";
  }

  return "upsell";
};

/** Records that an onboarding checkout has already moved past the native handoff prompt. */
const completeNativeAppDownloadForOnboardingCheckout = (store: AppStore): void => {
  store.platformInfo.perDeviceOnboardingInfo.completeOrSkipNativeAppDownload();
};

/** Advances the active Proactive trial checkout into the in-app Mem Agent onboarding flow. */
const completeFloatingMemCheckoutOnboarding = ({ store }: { store: AppStore }): void => {
  store.platformInfo.perDeviceOnboardingInfo.completeOrSkipProactiveTrialOnboarding();
};

const prepareFloatingMemForOnboardingCheckout = async ({
  store,
}: {
  store: AppStore;
}): Promise<boolean> => {
  return prepareFloatingMemForOnboarding({ store });
};

const reopenProactiveUpsellUnlessOnboardingOwnsCheckout = ({
  checkoutOnboardingStep,
  checkoutStartedFromOnboarding = false,
  store,
}: {
  checkoutOnboardingStep?: StripeCheckoutOnboardingStepType;
  checkoutStartedFromOnboarding?: boolean;
  store: AppStore;
}): void => {
  if (
    checkoutReturnBelongsToOnboarding({
      checkoutOnboardingStep,
      checkoutStartedFromOnboarding,
      store,
    })
  ) {
    return;
  }

  store.subscriptions.reopenProactiveUpsellAfterCheckoutReturn();
};

export function handleProactiveSubscriptionCancelReturn({
  checkoutOnboardingStep,
  checkoutStartedFromOnboarding = false,
  checkoutStartedFromUpgradePlanFlow = false,
  store,
}: {
  checkoutOnboardingStep?: StripeCheckoutOnboardingStepType;
  checkoutStartedFromOnboarding?: boolean;
  checkoutStartedFromUpgradePlanFlow?: boolean;
  store: AppStore;
}): void {
  clearPendingReminderShillUpsellAttribution();

  if (checkoutStartedFromOnboarding) {
    completeNativeAppDownloadForOnboardingCheckout(store);
  }

  if (checkoutStartedFromUpgradePlanFlow) {
    store.subscriptions.resetProactiveCheckoutProgress();
    store.subscriptions.openProactiveUpsell();
  } else if (
    checkoutReturnBelongsToOnboarding({
      checkoutOnboardingStep,
      checkoutStartedFromOnboarding,
      store,
    })
  ) {
    store.subscriptions.resetProactiveCheckoutProgress();
  } else {
    reopenProactiveUpsellUnlessOnboardingOwnsCheckout({
      checkoutOnboardingStep,
      checkoutStartedFromOnboarding,
      store,
    });
  }
  if (!checkoutStartedFromUpgradePlanFlow) {
    toastModule.triggerToast({
      content: PROACTIVE_SUBSCRIPTION_RETURN_CANCELED_MESSAGE,
    });
  }
}

async function dismissMemAgentCalloutAfterCheckout({ store }: { store: AppStore }): Promise<void> {
  try {
    await store.memAgentCallout.dismissIfPresent();
  } catch (unknownError) {
    const error = unknownError as Error;

    logger.warn({
      message: "[confirmProactiveSubscriptionReturn] Failed to dismiss Mem Agent callout",
      info: { error: objectModule.safeErrorAsJson(error) },
    });
  }
}

export async function confirmProactiveSubscriptionReturn({
  store,
  checkoutOnboardingStep,
  checkoutStartedFromOnboarding = false,
  checkoutStartedFromUpgradePlanFlow = false,
  stripeSessionId,
  originalPlanKind,
  originalEffectivePlanKind,
}: {
  store: AppStore;
  checkoutOnboardingStep?: StripeCheckoutOnboardingStepType;
  checkoutStartedFromOnboarding?: boolean;
  checkoutStartedFromUpgradePlanFlow?: boolean;
  stripeSessionId?: string | null;
  originalPlanKind?: SubscriptionPlanKindType;
  originalEffectivePlanKind?: SubscriptionPlanKindType;
}): Promise<boolean> {
  // Capture this before subscription reconciliation: successful confirmation
  // can complete the trial step and reveal the next in-app onboarding step.
  const checkoutBelongsToOnboarding = checkoutReturnBelongsToOnboarding({
    checkoutOnboardingStep,
    checkoutStartedFromOnboarding,
    store,
  });
  const checkoutBelongsToExistingUserProactiveTrial =
    checkoutReturnBelongsToExistingUserProactiveTrial({ checkoutOnboardingStep, store });

  if (checkoutBelongsToOnboarding) {
    store.onboarding.startProactiveCheckoutReturnConfirmation();
  }

  try {
    const confirmed = await confirmStripeCheckoutReturn({
      isConfirmed: () => store.subscriptions.billingSubscription?.isStoredProactive === true,
      store,
    });
    const billingSubscription = store.subscriptions.billingSubscription;

    if (confirmed && billingSubscription?.isStoredProactive) {
      const pricingAssignmentPromise = store.subscriptions.fetchProactivePricingAssignment({
        trackExposure: false,
      });

      // Proactive reconciliation can upgrade Mem Agent account access server-side.
      // Refresh that cache before deciding whether the dedicated onboarding is available.
      if (checkoutBelongsToOnboarding) {
        await store.spaceAccounts.fetchMySpaceAccounts();
      }

      const floatingMemOnboardingOwnsSuccessfulCheckout =
        checkoutBelongsToOnboarding &&
        !checkoutBelongsToExistingUserProactiveTrial &&
        store.floatingAgent.isOnboardingSurfaceAvailable;

      store.platformInfo.setSubscriptionCompletedSuccessfully(SubscriptionPlanKind.PROACTIVE);
      trackEvent(TrackedEvent.PaywallUpgradeReturn, {
        ...buildProactivePaywallEventPayload({
          store,
          originalPlanKind,
          originalEffectivePlanKind,
        }),
        stripe_session_id: stripeSessionId ?? undefined,
      });
      const pricingAssignment = await pricingAssignmentPromise;
      const pricingAssignmentIsAuthoritative =
        pricingAssignment.source !== PROACTIVE_PRICING_CLIENT_FALLBACK_SOURCE;
      const subscriptionPriceUsd =
        pricingAssignmentIsAuthoritative && pricingAssignment.currency === "usd"
          ? pricingAssignment.display_price_cents / 100
          : undefined;

      trackProactiveAccessEnabled({
        billingProvider: billingSubscription.provider,
        checkoutSource: resolveProactiveConversionCheckoutSource({
          checkoutBelongsToExistingUserProactiveTrial,
          checkoutBelongsToOnboarding,
          checkoutStartedFromUpgradePlanFlow,
        }),
        currentPeriodStart: billingSubscription.currentPeriodStart,
        planVariant: billingSubscription.planVariant,
        pricingExperimentKey: pricingAssignmentIsAuthoritative
          ? pricingAssignment.experiment_key
          : undefined,
        pricingExperimentVariantKey: pricingAssignmentIsAuthoritative
          ? pricingAssignment.variant_key
          : undefined,
        providerSubscriptionId: billingSubscription.providerSubscriptionId,
        spaceAccountId: store.spaceAccounts.myPersonalSpaceAccountId,
        stripeSessionId,
        subscriptionPriceUsd,
      });
      await dismissMemAgentCalloutAfterCheckout({ store });
      const reminderShillUpsellAttributionContext = readPendingReminderShillUpsellAttribution();
      await recordPendingReminderShillUpsellAttribution({
        stripeCheckoutSessionId: stripeSessionId,
      });
      await dismissReminderShillUpsellAttributionContext({
        context: reminderShillUpsellAttributionContext,
        store,
      });
      if (checkoutBelongsToOnboarding) {
        completeNativeAppDownloadForOnboardingCheckout(store);
      }

      if (floatingMemOnboardingOwnsSuccessfulCheckout) {
        const preparedFloatingMem = await prepareFloatingMemForOnboardingCheckout({ store });
        if (!preparedFloatingMem) {
          return true;
        }
        if (preparedFloatingMem) {
          store.platformInfo.perDeviceOnboardingInfo.activateMemAgentOnboarding();
        }
        completeFloatingMemCheckoutOnboarding({ store });
      }

      if (checkoutBelongsToExistingUserProactiveTrial) {
        store.onboarding.startExistingUserProactiveTrialWelcome();
        store.platformInfo.openWelcomeToProactiveModal();
      } else if (
        !floatingMemOnboardingOwnsSuccessfulCheckout &&
        !checkoutStartedFromUpgradePlanFlow
      ) {
        store.platformInfo.openWelcomeToProactiveModal();
      }

      return true;
    }

    trackEvent(TrackedEvent.PaywallUpgradeFailed, {
      ...buildProactivePaywallEventPayload({
        store,
        originalPlanKind,
        originalEffectivePlanKind,
      }),
      stripe_session_id: stripeSessionId ?? undefined,
      failure_stage: "checkout_return_confirmation",
      failure_reason: "not_confirmed",
    });
    clearPendingReminderShillUpsellAttribution();
    store.subscriptions.closeMemProactiveRedirectModal();
    reopenProactiveUpsellUnlessOnboardingOwnsCheckout({
      checkoutOnboardingStep,
      checkoutStartedFromOnboarding,
      store,
    });
    if (!checkoutStartedFromUpgradePlanFlow) {
      toastModule.triggerToast({
        content: PROACTIVE_SUBSCRIPTION_RETURN_NOT_CONFIRMED_MESSAGE,
      });
    }
    return false;
  } catch (unknownError) {
    const error = unknownError as Error;
    logger.error({
      message: "[confirmProactiveSubscriptionReturn] Failed to confirm Proactive subscription",
      info: { error: objectModule.safeErrorAsJson(error) },
    });
    trackEvent(TrackedEvent.PaywallUpgradeFailed, {
      ...buildProactivePaywallEventPayload({
        store,
        originalPlanKind,
        originalEffectivePlanKind,
      }),
      stripe_session_id: stripeSessionId ?? undefined,
      failure_stage: "checkout_return_confirmation",
      failure_reason: "confirmation_error",
      error_message: error.message,
    });

    store.subscriptions.closeMemProactiveRedirectModal();
    reopenProactiveUpsellUnlessOnboardingOwnsCheckout({
      checkoutOnboardingStep,
      checkoutStartedFromOnboarding,
      store,
    });
    clearPendingReminderShillUpsellAttribution();
    toastModule.triggerToast({
      content: "We couldn't confirm Proactive yet. Try again from Mem Agent settings.",
    });
    return false;
  } finally {
    if (checkoutBelongsToOnboarding) {
      store.onboarding.finishProactiveCheckoutReturnConfirmation();
      store.subscriptions.resetProactiveCheckoutProgress();
    }
  }
}
